/**
 * CreateCustomerModal — M2.4 · Aggiungi cliente manualmente al CRM.
 *
 * Modal generica (non WhatsApp-specific): usata da AdminCustomers e
 * StaffCustomers. Backend fa dedup su phone normalizzato.
 *
 * UX:
 *   - Nome*, Cognome, Telefono*, Email.
 *   - Phone normalizzato server-side ({{normalize_italian_phone}}).
 *   - Se numero già associato → mostra cliente esistente + link "Apri".
 *   - Se email già associata → warning, ma consente creazione se conferma.
 */
import React, { useState } from "react";
import { UserPlus, X, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";


export function CreateCustomerModal({ isOpen, onClose, onCreated, existingUrlBase = "/admin/customers" }) {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duplicate, setDuplicate] = useState(null);   // {customer_id, name, surname, phone}

  const reset = () => {
    setName(""); setSurname(""); setPhone(""); setEmail("");
    setError(null); setDuplicate(null); setLoading(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError("Il nome è obbligatorio"); return; }
    if (!phone.trim()) { setError("Il telefono è obbligatorio"); return; }
    setError(null); setDuplicate(null); setLoading(true);
    try {
      const { data } = await api.post("/admin/customers", {
        name: name.trim(),
        surname: surname.trim() || null,
        phone: phone.trim(),
        email: email.trim() || null,
      });
      onCreated({
        id: data.id,
        name: data.name,
        surname: data.surname,
        phone: data.phone,
      });
      reset(); onClose();
    } catch (err) {
      const resp = err?.response?.data;
      // Backend ritorna 409 con {duplicate_of: {id, name, surname, phone}}
      if (err?.response?.status === 409 && resp?.duplicate_of) {
        setDuplicate(resp.duplicate_of);
        setError(null);
      } else {
        setError(formatApiError(err));
      }
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;
  return (
    <div data-testid="create-customer-modal"
         onClick={handleClose}
         className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-3xl border-2 border-sand-300 shadow-2xl w-full max-w-md p-6 relative">
        <button onClick={handleClose}
                data-testid="create-customer-close"
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-sand-100">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-full bg-bronze-100 border-2 border-bronze-300 flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-bronze-700" />
          </div>
          <h2 className="font-serif text-2xl">Aggiungi cliente</h2>
        </div>

        {duplicate ? (
          <div className="space-y-4" data-testid="create-customer-duplicate">
            <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 text-sm">
              <div className="flex items-center gap-2 text-amber-900 font-semibold mb-2">
                <AlertTriangle className="w-4 h-4" /> Numero già in CRM
              </div>
              <div className="text-amber-800">
                <div>Questo numero è già associato a:</div>
                <div className="mt-2 font-semibold">
                  {duplicate.name} {duplicate.surname || ""}
                </div>
                <div className="text-xs mt-1">Tel: {duplicate.phone}</div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setDuplicate(null); setPhone(""); }}
                      className="px-4 py-2 rounded-full border-2 border-sand-300 text-sm hover:bg-sand-100">
                Cambia numero
              </button>
              <a href={`${existingUrlBase}/${duplicate.id}`}
                  data-testid="create-customer-open-existing"
                  className="px-4 py-2 rounded-full bg-bronze-600 text-white text-sm font-semibold hover:bg-bronze-700 inline-flex items-center">
                Apri cliente
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1">Nome *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                     required autoFocus data-testid="create-customer-name"
                     className="w-full rounded-full border-2 border-sand-300 px-4 py-2.5 text-sm focus:border-bronze-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1">Cognome</label>
              <input type="text" value={surname} onChange={(e) => setSurname(e.target.value)}
                     data-testid="create-customer-surname"
                     className="w-full rounded-full border-2 border-sand-300 px-4 py-2.5 text-sm focus:border-bronze-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1">Telefono *</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                     required placeholder="3487863782 oppure +393487863782"
                     data-testid="create-customer-phone"
                     className="w-full rounded-full border-2 border-sand-300 px-4 py-2.5 text-sm focus:border-bronze-500 outline-none" />
              <div className="text-[10px] text-ink-400 mt-1 pl-2">
                Se locale (10 cifre inizianti con 3), aggiungiamo +39 automaticamente.
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1">Email (opzionale)</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="create-customer-email"
                     className="w-full rounded-full border-2 border-sand-300 px-4 py-2.5 text-sm focus:border-bronze-500 outline-none" />
            </div>
            {error && (
              <div data-testid="create-customer-error"
                   className="rounded-2xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={handleClose}
                      className="px-4 py-2 rounded-full border-2 border-sand-300 text-sm hover:bg-sand-100">
                Annulla
              </button>
              <button type="submit" disabled={loading}
                      data-testid="create-customer-submit"
                      className="px-4 py-2 rounded-full bg-bronze-600 text-white text-sm font-semibold hover:bg-bronze-700 disabled:opacity-50 flex items-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Aggiungi cliente
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
